import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Droplets, Flame, Gauge, ArrowRight, CheckCircle, ShieldCheck, Home } from 'lucide-react';

const services = [
  {
    id: 'plumbing',
    number: '01',
    name: 'Plumbing',
    icon: Droplets,
    description: 'Complete residential and light commercial plumbing services in Calgary. From everyday repairs to renovations and full re-piping projects.',
    path: '/plumbing',
    services: [
      'Poly-B Replacement',
      'Tankless & Tank Water Heaters',
      'Drain Cleaning & Leak Repair',
      'Bathroom & Kitchen Plumbing',
      'Renovation Plumbing',
      'New Home & Basement Suites',
    ],
    image: '/images/dnb/plumbing-repair-1.jpg',
  },
  {
    id: 'heating',
    number: '02',
    name: 'Heating',
    icon: Flame,
    description: 'Furnace, boiler, hydronic and in-floor radiant heating systems. Keep your home or business warm and efficient through Calgary winters.',
    path: '/heating',
    services: [
      'Furnace Installation & Repair',
      'Boiler &  Hydronic Systems',
      'In-Floor Radiant Heating',
      'Garage Heaters',
      'Thermostats &  Controls',
      'Hot Water Tanks',
    ],
    image: '/images/dnb/furnace-1.jpg',
  },
  {
    id: 'gas-fitting',
    number: '03',
    name: 'Gas Fitting',
    icon: Gauge,
    description: 'Licensed gas fitting work for installations, hookups and safety. Whether you need a BBQ line, fireplace, or appliance install — we handle it.',
    path: '/gas-fitting',
    services: [
      'Gas Line Installation',
      'Gas Safety & Testing',
      'BBQ & Patio Lines',
      'Gas Appliance Hookups',
      'Gas Fireplace Connections',
      'Garage Heater Gas Lines',
    ],
    image: '/images/dnb/gas-line-1.jpg',
  },
];

const additionalServices = [
  { name: 'Backflow Testing', icon: ShieldCheck, path: '/plumbing/backflow-testing-calgary', desc: 'City of Calgary certified' },
  { name: 'Secondary Suite Plumbing', icon: Home, path: '/plumbing/secondary-suite-plumbing-calgary', desc: 'Code-compliant suite installs' },
];

export const ServiceCards = () => {
  return (
    <section data-testid="services-section" className="py-24 bg-white relative overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <span className="eyebrow mb-5">What We Do</span>
          <h2 className="font-heading font-extrabold text-4xl sm:text-5xl text-[#0F172A] mt-5 mb-5 leading-tight">
            Three services. <br className="hidden sm:block" />
            <span className="text-[#0284C7]">One trusted team.</span>
          </h2>
          <p className="text-slate-600 text-lg">
            Plumbing, heating, and gas fitting &mdash; for residential and light commercial customers across Calgary &amp; area.
          </p>
        </motion.div>

        {/* Numbered alternating service rows */}
        <div className="space-y-12 lg:space-y-20">
          {services.map((service, idx) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center ${
                idx % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
            >
              {/* Image / Visual side */}
              <div className={`lg:col-span-5 ${idx % 2 === 1 ? 'lg:order-2' : ''}`}>
                <div className="relative">
                  {/* Number background */}
                  <div className={`absolute ${idx % 2 === 1 ? '-right-4 -top-8' : '-left-4 -top-8'} step-number select-none z-0`}>
                    {service.number}
                  </div>
                  {/* Image card */}
                  <div className="relative overflow-hidden rounded-tl-[3rem] rounded-br-[3rem] rounded-tr-xl rounded-bl-xl border border-slate-200 shadow-xl shadow-slate-200/50 bg-slate-50 aspect-[4/3]">
                    <img
                      src={service.image}
                      alt={`${service.name} services in Calgary by DNB`}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                    {/* Overlay gradient */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-[#0C4A6E]/40 via-transparent to-transparent"></div>
                    {/* Big icon */}
                    <div className="absolute bottom-5 left-5 droplet-icon droplet-icon-lg">
                      <service.icon className="w-9 h-9" strokeWidth={2} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Content side */}
              <div className={`lg:col-span-7 ${idx % 2 === 1 ? 'lg:order-1' : ''}`}>
                <span className="text-[#0284C7] uppercase tracking-widest text-xs font-bold">
                  Service {service.number}
                </span>
                <h3 className="font-heading font-extrabold text-3xl sm:text-4xl text-[#0F172A] mt-2 mb-4 leading-tight">
                  {service.name}
                </h3>
                <p className="text-slate-600 text-lg mb-6 leading-relaxed">
                  {service.description}
                </p>

                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5 mb-7">
                  {service.services.map((item) => (
                    <li key={item} className="flex items-start gap-2.5">
                      <CheckCircle className="w-5 h-5 text-[#0284C7] flex-shrink-0 mt-0.5" strokeWidth={2} />
                      <span className="text-slate-700 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to={service.path}
                  data-testid={`service-${service.id}-link`}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  Explore {service.name} Services
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional services - compact row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-20 grid grid-cols-1 sm:grid-cols-2 gap-5"
        >
          {additionalServices.map((s) => (
            <Link
              key={s.name}
              to={s.path}
              className="dnb-card-soft flex items-center gap-4 group"
            >
              <div className="droplet-icon droplet-icon-sm droplet-icon-light flex-shrink-0">
                <s.icon className="w-5 h-5" strokeWidth={2} />
              </div>
              <div className="flex-1">
                <div className="font-heading font-bold text-[#0F172A] group-hover:text-[#0284C7] transition-colors">
                  {s.name}
                </div>
                <div className="text-sm text-slate-500">{s.desc}</div>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-[#0284C7] group-hover:translate-x-1 transition-all" />
            </Link>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ServiceCards;
